import { forwardRef, useContext, useImperativeHandle } from 'react';
import { FormConfigType, MarketingPackageStage } from '@urbanx/agx-ui-components';
import type { MultiOptionButtonOption } from '@urbanx/agx-ui-components';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { beginMarketingPackage } from 'Api/Marketing/marketingApi';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useFormLoader } from 'hooks/useFormLoader';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import FileList from './FileList';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { mapPaymentStatus } from '../MarketingPackage/MarketingPackagePaymentStatusMapper';
import { getPOADocuments } from 'utils/vendorBuyerUtil';
import { formatCurrency } from 'utils/formatNumber';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import './ListingDetails.scss';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { CampaignDetailModel } from '@urbanx/agx.campaigns.client';
import { MarketingPackageProjection } from '@urbanx/agx.marketing.client';
import { getCampaignStageLabel, LISTING_DOCUMENT_TYPES } from './ListingDetails.helpers';
import type { ListingDetailsHandle } from './ListingDetails.types';

export type { ListingDetailsHandle } from './ListingDetails.types';

dayjs.extend(relativeTime);

interface ListingDetailsProps {
  mobile?: boolean;
  campaign: CampaignDetailModel;
  marketingPackages: MarketingPackageProjection[];
  setSelectedMarketingPackage: (marketingPackage: MarketingPackageProjection) => void;
}

const ListingDetails = forwardRef<ListingDetailsHandle, ListingDetailsProps>((props, ref) => {
  const { mobile = false, campaign, marketingPackages, setSelectedMarketingPackage } = props;

  const { getAuthToken } = useContext(AuthContext);
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();
  const { propertyImageUrls, documents = [], completionState, vendors } = campaign;
  const { addMarketingToLiveListing } = useFeatureToggles(campaign?.state);
  const { loadForm } = useFormLoader();
  const imageUrl = propertyImageUrls?.medium ?? '';
  const isDesktop = windowSize === Breakpoints.Desktop;
  const getFileLink = useGetFileLink();

  const POADocuments = getPOADocuments(vendors);
  const combinedDocuments = [...documents, ...POADocuments];
  const filterListingDocuments = combinedDocuments
    .filter((document) => LISTING_DOCUMENT_TYPES.includes(document.documentType!))
    .map(
      (listingDocument) =>
        ({
          text: listingDocument.fileName,
          documentType: listingDocument.documentType,
          onClick: async () => {
            const fileLink = await getFileLink(listingDocument.containerFilePath);
            if (fileLink !== null) {
              openFileInNewTab(isDesktop, fileLink);
            }
          },
        }) as MultiOptionButtonOption
    );

  const onBeginMarketingPackage = async () => {
    const authToken = await getAuthToken();
    if (!authToken) return null;
    const { formId } = await beginMarketingPackage(authToken, dispatch, campaign.campaignId ?? '');
    await loadForm(campaign.campaignId ?? '', formId, FormConfigType.Marketing);
  };

  useImperativeHandle(ref, () => ({
    addMarketing: onBeginMarketingPackage,
  }));

  return (
    <>
      <div
        style={{
          backgroundImage: `url("${imageUrl || placeHolderImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          aspectRatio: '2/1',
        }}
        className={`imageCard-image ${completionState?.completionType ? 'archived' : ''}`}
      >
        <div className="image-tag">
          <AgxTypography
            id="listing-details-stage-label"
            type="label"
            size="medium"
            color="var(--brand-text-button-color)"
          >
            {getCampaignStageLabel(campaign.stage)}
          </AgxTypography>
        </div>
      </div>

      {addMarketingToLiveListing && (
        <AgxSpacer direction="vertical" fill gap="md">
          <AgxTypography id="listing-details-marketing-title" type="label" size="medium">
            Marketing
          </AgxTypography>
          {marketingPackages.length > 0 ? (
            <AgxSpacer direction="vertical" fill gap="xs">
              {marketingPackages.map((marketingPackage, index) => (
                <AgxSpacer
                  id={marketingPackage.id}
                  data-testid={`marketingPackage-${index}`}
                  key={marketingPackage.id}
                  direction="horizontal"
                  gap="spacing-ml"
                  onClick={() => setSelectedMarketingPackage(marketingPackage)}
                  fill
                  style={{
                    border: '1px solid var(--border-secondary)',
                    borderRadius: 'var(--radius-3xl)',
                  }}
                  padding="xs"
                >
                  <Icon_SparkleForm width={50} height={50} />
                  <AgxSpacer direction="horizontal" contentJustification="space-between" fill>
                    <AgxSpacer className="marketingPackageName" direction="vertical">
                      <AgxSpacer direction="horizontal">
                        <AgxTypography
                          dataTestId={`status-${index}`}
                          id={`status-${index}`}
                          type="caption"
                          size="medium"
                          color="var(--text-tertiary)"
                        >
                          {marketingPackage.stage?.value}
                        </AgxTypography>
                      </AgxSpacer>
                      <AgxSpacer direction="horizontal">
                        <AgxTypography
                          id={`marketing-package-name-${index}`}
                          type="body"
                          size="medium"
                          overflowEllipsis
                        >
                          {marketingPackage?.packageTemplateName
                            ? marketingPackage?.packageTemplateName
                            : marketingPackage?.packageDisplayName}{' '}
                          - {formatCurrency(getMarketingPackageTotal(marketingPackage), 0)}
                        </AgxTypography>
                      </AgxSpacer>
                    </AgxSpacer>
                    <AgxSpacer direction="vertical">
                      {marketingPackage.stage?.value === MarketingPackageStage.Complete &&
                      !!marketingPackage.displayStatus ? (
                        mapPaymentStatus(marketingPackage.displayStatus)
                      ) : (
                        <AgxTypography
                          id={`marketing-package-last-updated-${index}`}
                          type="caption"
                          size="medium"
                          color="var(--neutral-grey-700)"
                        >
                          {dayjs(marketingPackage.lastUpdated).fromNow()}
                        </AgxTypography>
                      )}
                    </AgxSpacer>
                  </AgxSpacer>
                </AgxSpacer>
              ))}
            </AgxSpacer>
          ) : (
            <AgxSpacer
              className="noMarketingStyle"
              direction="horizontal"
              contentJustification="center"
            >
              <AgxTypography id="listing-details-no-marketing" type="label" size="medium">
                There is no marketing.
              </AgxTypography>
            </AgxSpacer>
          )}
          <AgxButton
            id="agx-addMarketingButton"
            text={`Add Marketing`}
            variant="primary"
            size="medium"
            onClick={onBeginMarketingPackage}
            stretch
          />
        </AgxSpacer>
      )}
      <AgxSpacer direction="vertical" fill className="fileListContainer">
        <FileList files={filterListingDocuments} title="Listing Documents" />
      </AgxSpacer>
    </>
  );
});

export default ListingDetails;
